import { supportedUILanguages } from 'globalConstants';
import { i18nBase } from 'services/Localization/i18n';
import Language from 'models/users/language';

export default class UserLanguage extends Language {
  private static readonly _fallbackUserLanguage = 'en'; // must match with i18nBase.options.fallbackLng

  private static readonly _fallbackUserLanguageWithCulture = 'en-EN';

  private static readonly _supportedLanguageIds = new Map<string, number>([
    ['en', 1],
    ['de', 23],
    ['nl', 82],
  ]);

  codeWithCulture: string;

  constructor(code: string) {
    super();

    // 1. when code is empty, set language to fallback language
    // 2. set language to full supported language when length > 2 and match is found
    // 3. set language to best fit based on language code only
    // 4. set language to fallback language

    if (!code || code.length < 2) {
      this.codeWithCulture = UserLanguage._fallbackUserLanguageWithCulture;
      this.code = UserLanguage._fallbackUserLanguage;
    } else {
      if (UserLanguage.hasFullSupport(code)) {
        this.codeWithCulture = code;
        this.code = UserLanguage.getLanguageCode(code);
      } else if (UserLanguage.hasLanguageSupport(code)) {
        this.codeWithCulture = UserLanguage.getBestLangMatch(code);
        this.code = UserLanguage.getLanguageCode(code);
      } else {
        this.codeWithCulture = UserLanguage._fallbackUserLanguageWithCulture;
        this.code = UserLanguage._fallbackUserLanguage;
      }
    }

    this.name = supportedUILanguages.get(this.code) as string;
  }

  clone(): UserLanguage {
    const output = new UserLanguage(this.code);
    output.languageId = this.languageId;

    return output;
  }

  static getLanguageCode(language: string): string {
    // return the language without culture
    if (!language || language.length < 2) {
      language = UserLanguage._fallbackUserLanguage;
    }
    if (language.length > 2) {
      return language.substring(0, 2);
    }

    return language;
  }

  static getSupportedLanguages(): Map<string, string> {
    return supportedUILanguages;
  }

  static getSupportedFullLanguages(): string[] {
    return (i18nBase.options.supportedLngs as string[]) || [UserLanguage._fallbackUserLanguage];
  }

  static getBrowserLanguage(): string {
    //return 2-letter language code detected by browser
    //when not supported by the UI, return the fallback language
    const lang = this.getLanguageCode(navigator.language);
    if (this.hasLanguageSupport(lang)) {
      return lang;
    } else {
      return UserLanguage._fallbackUserLanguage;
    }
  }

  static getFallBack(): string {
    return UserLanguage._fallbackUserLanguage;
  }

  static getLanguageId(languageCode: string): number | undefined {
    return UserLanguage._supportedLanguageIds.get(UserLanguage.getLanguageCode(languageCode));
  }

  static getBestLangMatch(languageCode: string): string {
    // returns the first supported language with culture based on the code
    // 2-letter languages must be at the end of the array
    const lngs = this.getSupportedFullLanguages();

    for (const lng of lngs) {
      if (this.getLanguageCode(lng).toLowerCase() === languageCode.toLowerCase()) {
        return lng;
      }
    }

    return UserLanguage._fallbackUserLanguageWithCulture;
  }

  static hasFullSupport(code: string): boolean {
    //returns true when the full code - language and culture - are configured on the localization service
    if (!code || code.length < 5) {
      return false;
    }
    const lngs = this.getSupportedFullLanguages();

    return lngs.map((l) => l.toLowerCase()).indexOf(code.toLowerCase()) >= 0;
  }

  static hasLanguageSupport(languageCode: string): boolean {
    //returns true when the language part of the code (languageCode) is supported
    return supportedUILanguages.has(languageCode.toLowerCase());
  }
}
